import { forwardRef, type ElementType, type HTMLAttributes, type ReactNode } from 'react';

import styles from './Surface.module.css';

/**
 * Surface — an OPAQUE card, and nothing else.
 *
 * `bg.gif` sits behind every screen (`AppShell.module.css`), so any content
 * region with no fill of its own inherits a moving image underneath its
 * text. Spec D-3: the fix is an opaque ground, never a translucent one — a
 * 90%-opacity tint (the existing `--color-surface-veiled` panel family) still
 * lets the frame underneath animate through the text, which is the
 * readability problem itself, not a mitigation of it. `Surface` therefore
 * reads `--color-surface` at full strength; it has no `tone`, no opacity
 * prop and no way to reach the veiled tokens — a caller who wants a card
 * that still lets the ground show through is reaching for the wrong
 * primitive.
 *
 * A single class, tokens only: fill, a hairline edge for definition against
 * an equally-dark neighbour, and a radius. No padding is imposed — content
 * that needs breathing room adds its own, the same way `AttributeTag` leaves
 * layout to its caller.
 *
 * `as` is the one escape hatch, and it changes the ELEMENT, never the class:
 * `Pagination` is a `nav` landmark that needed the opaque card underneath it
 * without losing that role to an extra wrapping `div`.
 */

export interface SurfaceProps extends HTMLAttributes<HTMLElement> {
  /** The rendered element. Defaults to `div`; the class and its rule never change. */
  as?: ElementType;
  children?: ReactNode;
}

export const Surface = forwardRef<HTMLElement, SurfaceProps>(function Surface(
  { as: Component = 'div', children, className, ...rest },
  ref,
) {
  return (
    <Component
      {...rest}
      ref={ref}
      className={[styles['surface'], className].filter(Boolean).join(' ')}
    >
      {children}
    </Component>
  );
});
